import type { Sale } from "src/interfaces/sale.interface";
import type {
  Transaction,
  MappedTransaction,
  TransactionWithDetails,
} from "src/interfaces/transaction.interface";
import type { User } from "src/interfaces/systemState.interface";
import type { Vendor } from "src/interfaces/vendor.interface";
import type { GeneralLedgerAccountV2Type } from "src/interfaces/generalLedgerAccount.interface";
import {
  TransfersAccountId,
  TransfersAccountName,
} from "src/utils/general-ledger-accounts";
import {
  endOfCalendarDayContainingInstant,
  formatZonedDate,
  getEffectiveTimeZoneFromUser,
  startOfCalendarDayContainingInstant,
} from "src/utils/timezone";

export interface DateParametersProfitLoss {
  startDate: Date;
  endDate: Date;
  compareStartDate?: Date;
  compareEndDate?: Date;
}

export interface DateParametersInventory {
  year: number;
}

export interface ParametersPlatformTransaction {
  startDate: Date;
  endDate: Date;
  platform?: string;
}

export interface ParametersConsignmentCommission {
  startDate: Date;
  endDate: Date;
  consignor?: Vendor;
}

export interface ParametersSalesTax {
  startDate: Date;
  endDate: Date;
}

export interface ParametersScheduleC {
  year: number;
}

export interface ParametersBalanceSheet {
  date: Date;
}

export interface ParametersProfitLossComparison {
  startDate: Date;
  endDate: Date;
  groupBy?: "month" | "quarter";
}

export type ReportParameters = DateParametersProfitLoss &
  DateParametersInventory &
  ParametersPlatformTransaction &
  ParametersConsignmentCommission &
  ParametersSalesTax &
  ParametersScheduleC &
  ParametersBalanceSheet &
  ParametersProfitLossComparison;

function renderZonedOnlyDate(date: Date, timeZone: string): string {
  return formatZonedDate(date, timeZone, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

export interface ReportDetails {
  title: string;
  titleFull: string;
  filename: string;
  dateRange: string;
}

export interface ProfitLossReportData {
  reportDetails: ReportDetails;
  salesByDepartment: Record<string, number>;
  COGSDetail: Record<string, number>;
  expenseDetail: any; // TODO
  totalSales: number;
  totalCOGS: number;
  totalExpenses: number;
  grossProfit: number;
  estimatedIncomeTax: number;
  netProfit: number;
  netProfitNoIncomeTax: number;
  salesListByDepartment: Record<string, Sale[]>;
  transactionsListByExpense: Record<string, Transaction[]>;
}

export interface ProfitLossReport {
  type: "profit/loss";
  data: ProfitLossReportData[];
}

export interface ProfitLossComparisonReport {
  type: "profit/loss-comparison";
  groupBy: "month" | "quarter";
  data: ProfitLossReportData[];
}

export interface InventoryReportData {
  beginning: {
    amount: number;
    transactions: MappedTransaction[];
  };
  during: {
    amount: number;
    transactions: MappedTransaction[];
  };
  end: {
    amount: number;
    transactions: MappedTransaction[];
  };
  cost: {
    amount: number;
    transactions: MappedTransaction[];
  };
  invalidPurchaseDateSales: Sale[];
}

export interface InventoryReport {
  type: "inventory";
  year: string; // yyyy
  start: Date;
  end: Date;
  data: InventoryReportData;
}

export interface PlatformTransactionRow {
  id: string;
  date: Date;
  transactionId: string;
  title: string;
  moneyIn?: number;
  moneyOut?: number;
  collection?: "sale" | "transaction";
  collectionId?: string;
  journal_entry_id?: string;
}

export interface PlatformTransactionReportData {
  reportDetails: ReportDetails;
  rows: PlatformTransactionRow[];
  totals: {
    sales: number;
    returns: number;
    expenses: number | Record<string, number>;
    transfers: number;
    other: number;
  };
  platform: string;
}

export interface PlatformTransactionReport {
  type: "platform-transaction";
  data: PlatformTransactionReportData;
}

export interface ConsignmentCommissionReportData {
  reportDetails: ReportDetails;
  consignor: Vendor;
  sales: Sale[];
  totals: {
    sales: number;
    returns: number;
    expenses: number;
    grossProfit: number;
    commission: number;
  };
}

export interface ConsignmentCommissionReport {
  type: "consignment-commission";
  data: ConsignmentCommissionReportData;
}

export interface SalesTaxReportData {
  reportDetails: ReportDetails;
  taxByState: Record<string, { liableToPay: number; nonLiableToPay: number }>;
  salesByState: Record<string, Sale[]>;
}

export interface SalesTaxReport {
  type: "sales-tax";
  data: SalesTaxReportData;
}

export interface ScheduleCReport {
  type: "schedule-c";
  parameters: ParametersScheduleC;
}

interface BalanceSheetAccountEntry {
  id: string;
  name: string;
  type: GeneralLedgerAccountV2Type;
  amount: number;
  transactions: MappedTransaction[];
}

export interface BalanceSheetReport {
  type: "balance-sheet";
  parameters: ParametersBalanceSheet;
  entries: {
    id: string;
    label: string;
    children: {
      id: string;
      label: string;
      children: BalanceSheetAccountEntry[];
      total: number;
    }[];
    total: number;
  }[];
}

export type Reports =
  | InventoryReport
  | ProfitLossReport
  | ProfitLossComparisonReport
  | PlatformTransactionReport
  | ConsignmentCommissionReport
  | SalesTaxReport
  | ScheduleCReport
  | BalanceSheetReport;

export type ReportTypes =
  | InventoryReport["type"]
  | ProfitLossReport["type"]
  | ProfitLossComparisonReport["type"]
  | PlatformTransactionReport["type"]
  | ConsignmentCommissionReport["type"]
  | SalesTaxReport["type"]
  | ScheduleCReport["type"]
  | BalanceSheetReport["type"];

export function reportIsProfitLoss(
  report: Reports
): report is ProfitLossReport {
  return (report as ProfitLossReport).type === "profit/loss";
}

export function reportIsProfitLossComparison(
  report: Reports
): report is ProfitLossComparisonReport {
  return (
    (report as ProfitLossComparisonReport).type === "profit/loss-comparison"
  );
}

export function reportIsInventory(report: Reports): report is InventoryReport {
  return (report as InventoryReport).type === "inventory";
}

export function reportIsPlatformTransaction(
  report: Reports
): report is PlatformTransactionReport {
  return (report as PlatformTransactionReport).type === "platform-transaction";
}

export function reportIsConsignmentCommission(
  report: Reports
): report is ConsignmentCommissionReport {
  return (
    (report as ConsignmentCommissionReport).type === "consignment-commission"
  );
}

export function reportIsSalesTax(report: Reports): report is SalesTaxReport {
  return (report as SalesTaxReport).type === "sales-tax";
}

export function reportIsScheduleC(report: Reports): report is ScheduleCReport {
  return (report as ScheduleCReport).type === "schedule-c";
}

export function reportIsBalanceSheet(
  report: Reports
): report is BalanceSheetReport {
  return (report as BalanceSheetReport).type === "balance-sheet";
}

function isBalanceTransferTransaction(transaction: Transaction): boolean {
  return (
    transaction.gl_account_id === TransfersAccountId ||
    transaction.account === TransfersAccountName
  );
}

function moneyColumnsFromValues(
  ...values: number[]
): Pick<PlatformTransactionRow, "moneyIn" | "moneyOut"> {
  const columns: Pick<PlatformTransactionRow, "moneyIn" | "moneyOut"> = {};

  for (const value of values) {
    if (value > 0) {
      columns.moneyIn = (columns.moneyIn ?? 0) + value;
    } else if (value < 0) {
      columns.moneyOut = (columns.moneyOut ?? 0) + value;
    }
  }

  return columns;
}

export function buildPlatformTransactionReport(
  reportType: "platform-transaction",
  parameters: ParametersPlatformTransaction,
  sales: Sale[],
  transactions: TransactionWithDetails[],
  user?: User
): PlatformTransactionReport {
  if (!parameters.platform) {
    return {
      type: "platform-transaction",
      data: {
        reportDetails: {
          title: "",
          titleFull: "",
          filename: "",
          dateRange: "",
        },
        rows: [],
        totals: {
          sales: 0,
          returns: 0,
          expenses: 0,
          transfers: 0,
          other: 0,
        },
        platform: "",
      },
    };
  }
  const timeZone = getEffectiveTimeZoneFromUser(user);
  const startDate = startOfCalendarDayContainingInstant(
    new Date(parameters.startDate),
    timeZone
  );
  const endDate = endOfCalendarDayContainingInstant(
    new Date(parameters.endDate),
    timeZone
  );

  const totals = {
    sales: 0,
    returns: 0,
    expenses: 0,
    transfers: 0,
    other: 0,
  };
  const rows: PlatformTransactionRow[] = [];

  for (const sale of sales) {
    if (sale.sale_platform !== parameters.platform) continue;
    if (!(+sale.sale_price || 0)) continue;

    if (sale.original_id || sale.is_return) {
      if (!sale.return_date) continue;
      const date = new Date(sale.return_date || 0);
      if (date < startDate || date > endDate) continue;

      totals.returns += sale.sale_price;
      const feeTotal = -(
        (sale.transaction_fees || 0) + (sale.shipping_cost || 0)
      );
      totals.expenses += feeTotal;

      rows.push({
        id: sale.id,
        transactionId: sale.transaction_id || "",
        date,
        title: sale.item_title,
        collection: "sale",
        collectionId: sale.id,
        ...moneyColumnsFromValues(sale.sale_price, feeTotal),
      });
    } else {
      if (!sale.sale_date) continue;
      const date = new Date(sale.sale_date || 0);
      if (date < startDate || date > endDate) continue;

      totals.sales += sale.sale_price;
      const feeTotal = -(
        (sale.transaction_fees || 0) + (sale.shipping_cost || 0)
      );
      totals.expenses += feeTotal;

      rows.push({
        id: sale.id,
        transactionId: sale.transaction_id || "",
        date,
        title: sale.item_title,
        collection: "sale",
        collectionId: sale.id,
        ...moneyColumnsFromValues(sale.sale_price, feeTotal),
      });
    }
  }

  for (const transaction of transactions) {
    if (transaction.integration_name !== parameters.platform) continue;
    if (!transaction.date) continue;
    const transactionDate = new Date(transaction.date);
    if (transactionDate < startDate || transactionDate > endDate) continue;

    const amount = +transaction.amount || 0;
    if (amount === 0) continue;

    const accountType = transaction.accountDoc?.type?.[0];

    const rowBase: PlatformTransactionRow = {
      date: transaction.date,
      title: transaction.name || transaction.description,
      id: transaction.id,
      transactionId:
        transaction.transaction_id ||
        (transaction as any).transactionId ||
        (transaction as any).orderId ||
        "",
      collection: "transaction",
      collectionId: transaction.id,
      journal_entry_id: transaction.journal_entry_id,
    };

    if (isBalanceTransferTransaction(transaction)) {
      const displayValue = -amount;
      totals.transfers += displayValue;
      rows.push({ ...rowBase, ...moneyColumnsFromValues(displayValue) });
      continue;
    }

    if (accountType === "income") {
      const displayValue = -amount;
      if (amount < 0) {
        totals.sales += -amount;
      } else {
        totals.returns += -amount;
      }
      rows.push({ ...rowBase, ...moneyColumnsFromValues(displayValue) });
      continue;
    }

    if (accountType === "expenses") {
      const displayValue = -amount;
      totals.expenses += displayValue;
      rows.push({ ...rowBase, ...moneyColumnsFromValues(displayValue) });
      continue;
    }

    if (
      accountType === "assets" ||
      accountType === "liabilities" ||
      accountType === "equity" ||
      accountType === undefined
    ) {
      const displayValue = -amount;
      totals.other += displayValue;
      rows.push({ ...rowBase, ...moneyColumnsFromValues(displayValue) });
    }
  }

  rows.sort(
    (a: PlatformTransactionRow, b: PlatformTransactionRow) =>
      a.date.getTime() - b.date.getTime()
  );

  const title = "Platform Transaction Report";
  const dateRange = `${renderZonedOnlyDate(
    parameters.startDate,
    timeZone
  )} to ${renderZonedOnlyDate(parameters.endDate, timeZone)}`;
  return {
    type: "platform-transaction",
    data: {
      reportDetails: {
        title,
        titleFull: `${title} from ${dateRange} - ${parameters.platform}`,
        filename: `${title}-${dateRange}-${parameters.platform}.pdf`
          .toLowerCase()
          .replace(/[-/ ]/g, "-"),
        dateRange,
      },
      rows,
      totals,
      platform: parameters.platform,
    },
  };
}

export function buildConsignmentCommissionReport(
  reportType: "consignment-commission",
  parameters: ParametersConsignmentCommission,
  sales: Sale[],
  user?: User
): ConsignmentCommissionReport {
  if (!parameters.consignor) {
    return {
      type: "consignment-commission",
      data: {
        reportDetails: {
          title: "",
          titleFull: "",
          filename: "",
          dateRange: "",
        },
        consignor: {
          id: "",
          name: "",
          contractor: false,
          address: "",
          phone: [],
          email: [],
          user: "",
        },
        totals: {
          sales: 0,
          returns: 0,
          expenses: 0,
          grossProfit: 0,
          commission: 0,
        },
        sales: [],
      },
    };
  }

  const timeZone = getEffectiveTimeZoneFromUser(user);
  const startDate = startOfCalendarDayContainingInstant(
    new Date(parameters.startDate),
    timeZone
  );
  const endDate = endOfCalendarDayContainingInstant(
    new Date(parameters.endDate),
    timeZone
  );

  const totals = {
    sales: 0,
    returns: 0,
    expenses: 0,
    grossProfit: 0,
    commission: 0,
  };

  const filteredSales: Sale[] = [];

  for (const sale of sales) {
    if (sale.vendor !== parameters.consignor.id) continue;
    if (sale.original_id || sale.is_return) {
      if (!sale.return_date) continue;
      const date = new Date(sale.return_date || 0);
      if (date < startDate || date > endDate) continue;
      filteredSales.push(sale);
      totals.returns += +sale.sale_price || 0;
      totals.expenses +=
        +(sale.purchase_price || 0) +
        +(sale.transaction_fees || 0) +
        +(sale.shipping_cost || 0) +
        +(sale.other_fees || 0) +
        +(sale.shipping_cost_analytics || 0) +
        +(sale.extra_shipping_cost || 0) +
        +(sale.extra_transaction_fees || 0);
    } else {
      if (!sale.sale_date) continue;
      const date = new Date(sale.sale_date || 0);
      if (date < startDate || date > endDate) continue;
      filteredSales.push(sale);
      totals.sales += +sale.sale_price || 0;
      totals.expenses +=
        +(sale.purchase_price || 0) +
        +(sale.transaction_fees || 0) +
        +(sale.shipping_cost || 0) +
        +(sale.other_fees || 0) +
        +(sale.shipping_cost_analytics || 0);
    }
  }

  totals.grossProfit = totals.sales + totals.returns - totals.expenses;
  totals.commission =
    (totals.grossProfit * (parameters.consignor?.commission || 0)) / 100;

  const title = "Consignment Commission Report";
  const dateRange = `${renderZonedOnlyDate(
    parameters.startDate,
    timeZone
  )} to ${renderZonedOnlyDate(parameters.endDate, timeZone)}`;
  return {
    type: "consignment-commission",
    data: {
      reportDetails: {
        title,
        titleFull: `${title} from ${dateRange} - ${parameters.consignor.name}`,
        filename: `${title}-${dateRange}-${parameters.consignor.name}.pdf`
          .toLowerCase()
          .replace(/[-/ ]/g, "-"),
        dateRange,
      },
      sales: filteredSales,
      totals,
      consignor: parameters.consignor,
    },
  };
}

// This function is just a stub. Real report will be handled in the
// component that is rendered by `displayResults`.
// We do it differently for the Schedule C report because we need to show a dialog
// after the user has hit "Run Report". As this button isn't managed by the "ParametersSelection"
// component, we don't have any other easy way in order to implement the requirements.
export function buildScheduleCReport(
  reportType: ScheduleCReport["type"],
  parameters: ParametersScheduleC
): ScheduleCReport {
  return {
    type: reportType,
    parameters,
  };
}
